import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Save } from "lucide-react";

const emptyJob = {
  title: "", company: "", location: "Remote", employment_type: "Full-time",
  salary_range: "", description: "", required_skills: [], nice_to_have: [],
  min_experience_years: 0,
};

export const EditJobDialog = ({ open, onOpenChange, jobId, onSaved }) => {
  const [job, setJob] = useState(emptyJob);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !jobId) return;
    api.get(`/jobs/${jobId}`).then((r) => setJob({
      ...emptyJob,
      ...r.data,
      salary_range: r.data.salary_range || "",
      required_skills: r.data.required_skills || [],
      nice_to_have: r.data.nice_to_have || [],
    })).catch(() => {});
  }, [open, jobId]);

  const set = (k, v) => setJob((j) => ({ ...j, [k]: v }));
  const setList = (k, csv) => set(k, csv.split(",").map((s) => s.trim()).filter(Boolean));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title: job.title, company: job.company, location: job.location,
        employment_type: job.employment_type,
        salary_range: job.salary_range || null,
        description: job.description,
        required_skills: job.required_skills, nice_to_have: job.nice_to_have,
        min_experience_years: Number(job.min_experience_years) || 0,
      };
      await api.patch(`/jobs/${jobId}`, payload);
      toast.success("Job updated");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-job-dialog" className="bg-[#111827] border-white/10 text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Edit job</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 mt-2">
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Title" v={job.title} onChange={(v) => set("title", v)} testId="edit-title" />
            <Field label="Company" v={job.company} onChange={(v) => set("company", v)} testId="edit-company" />
            <Field label="Location" v={job.location} onChange={(v) => set("location", v)} testId="edit-location" />
            <Field label="Employment type" v={job.employment_type} onChange={(v) => set("employment_type", v)} testId="edit-emptype" />
            <Field label="Salary range" v={job.salary_range} onChange={(v) => set("salary_range", v)} testId="edit-salary" />
            <Field label="Min experience (years)" v={job.min_experience_years} onChange={(v) => set("min_experience_years", v)} type="number" testId="edit-minexp" />
          </div>
          <div>
            <Label className="text-slate-300 text-sm">Description</Label>
            <Textarea
              value={job.description}
              onChange={(e) => set("description", e.target.value)}
              data-testid="edit-description"
              rows={6}
              className="bg-[#0B0F19] border-white/10 mt-2"
            />
          </div>
          <Field label="Required skills (comma-separated)" v={job.required_skills.join(", ")} onChange={(v) => setList("required_skills", v)} testId="edit-skills" />
          <Field label="Nice to have (comma-separated)" v={job.nice_to_have.join(", ")} onChange={(v) => setList("nice_to_have", v)} testId="edit-nice" />
        </div>
        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-white/10">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="bg-transparent border-white/15 text-slate-100 hover:bg-white/5 hover:text-white rounded-full">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="edit-job-save-btn" className="bg-[#06B6D4] hover:bg-[#0891B2] text-[#0B0F19] rounded-full">
            <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, v, onChange, type = "text", testId }) => (
  <div>
    <Label className="text-slate-300 text-sm">{label}</Label>
    <Input
      value={v}
      type={type}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="bg-[#0B0F19] border-white/10 mt-2"
    />
  </div>
);
